import { z } from 'zod';
import { isoDate, uuid } from './posting.js';
/**
 * Der Starttag einer Buchungsautomatik — Auftrag `WH-bankaufladung`
 * (2026-09-10), Migration `0214`, Durchsetzungsfall `E-1223`.
 *
 * **Warum es diesen Vertrag gibt.** Bis zum 2026-09-10 stand der Tag, ab dem
 * die Aufladungsautomatik (Regel 1) selbst bucht, als Konstante
 * `AUFLADUNGSAUTOMATIK_AB` im Quelltext (`apps/api/src/cutover/kalender.ts`,
 * Wert: der Cutover-Tag `2026-10-01`). Der Auftraggeber hat am 2026-09-10
 * entschieden, dass die Automatik **ab dem Tag greift, an dem der Webhook
 * eingerichtet ist** — und dieser Tag ist am Tag der Landung nicht bekannt: Er
 * hängt daran, wann drüben ein Empfänger steht. Eine Konstante zu verschieben
 * hiesse, für ein Datum einen Chip zu schreiben, einen Zug zu fahren, ein
 * Abbild zu bauen und auszurollen — und niemand könnte danach sagen, wer es
 * wann entschieden hat.
 *
 * **Die Abwesenheit einer Einstellung ist eine Aussage.** Kein Eintrag heisst
 * nicht „ab sofort", sondern „es gilt der Rückfall" — und der Rückfall ist
 * genau der Wert der bisherigen Konstante. Das Feld {@link
 * automatikStartSchema} `quelle` sagt dem Leser, welcher der beiden Fälle
 * vorliegt, statt ihn raten zu lassen: Ein Datum allein unterscheidet
 * „entschieden" nicht von „geerbt".
 *
 * **Nachtrag 2026-09-10 (Auftrag `ZZ-automatikstart`, Migration `0216`, Fall
 * `E-1231`):** Der Katalog trägt seither einen **zweiten** Schlüssel,
 * `zahlung-zuordnung` — der nächtliche Takt der Zahlungszuordnung hielt bis
 * dahin die Konstante `ZAHLUNGSAUTOMATIK_AB`, und der Auftraggeber will eine
 * per Überweisung beglichene Altrechnung der gebuchten Klasse jetzt gebucht
 * haben statt erst ab dem Cutover-Tag. **Die zwei Schlüssel vergleichen ihren
 * Tag mit Verschiedenem** — Buchungsdatum gegen Lauftag —, und die Tabelle bei
 * {@link automatikStartSchluesselSchema} sagt je Schlüssel, womit.
 */
/**
 * Der Katalog der Automatiken, die einen setzbaren Starttag haben — **die eine
 * Quelle**.
 *
 * Zwei Werte heute. Die `CHECK`-Bedingung
 * `automatik_start_schluessel_bekannt` (Migration `0214`, erweitert durch
 * `0216`) schreibt sie notwendigerweise ein zweites Mal hin — eine Datenbank
 * kann kein TypeScript-Schema lesen; wer den Katalog erweitert, ändert beide
 * Orte im selben Commit. Dieselbe unvermeidliche Dublette wie beim
 * Ereigniskatalog der Webhooks (`webhooks.ts`).
 *
 * **`aufladung-bank` und nicht `aufladung`.** Der Name nennt die Automatik,
 * deren Grenze er verschiebt, samt Kanal — dieselbe Vorsicht wie beim
 * Ereignisnamen `guthaben.aufladung.bank` (`D-17`): Die Regeln 2 bis 5 haben
 * ihre eigenen Aktivierungsgrenzen (`REGELN234_AB`, `AZ_RUECKBUCHUNG_AB`), und
 * ein Schlüssel, der bloss `aufladung` hiesse, sähe aus, als gälte er für
 * mehr, als er tut.
 *
 * **`zahlung-zuordnung` — Auftrag `ZZ-automatikstart` (2026-09-10),
 * Auftraggeberentscheid vom selben Tag, Migration `0216`, Fall `E-1231`.** Der
 * nächtliche Takt der Zahlungszuordnung
 * (`apps/api/src/zahlungen/takt.ts`) hielt bis dahin die Konstante
 * `ZAHLUNGSAUTOMATIK_AB` — den Cutover-Tag. Der Anlass ist eine gemessene
 * Zahlung: Eine per Überweisung beglichene Altrechnung der **gebuchten**
 * Klasse steht als reiner Volltreffer (`D-14`) in der Klärungsliste und wird
 * nicht gebucht, weil der Takt bis zum Cutover-Tag keinen Durchgang fährt. Der
 * Auftraggeber will sie jetzt gebucht haben, und das ist dieselbe
 * Entscheidung, die `aufladung-bank` schon getragen hat: **Der Starttag wird
 * eine Einstellung.**
 *
 * ## Die zwei Schlüssel lesen ihren Tag VERSCHIEDEN, und das ist keine Unschärfe
 *
 * | Schlüssel | `abDatum` wird verglichen mit | Rückfall |
 * |---|---|---|
 * | `aufladung-bank` | dem **Buchungsdatum der Bewegung** (`bank_transaction.booking_date`) | `AUFLADUNGSAUTOMATIK_AB` |
 * | `zahlung-zuordnung` | dem **Kalendertag des nächtlichen Laufs** (der Slotkennung) | `ZAHLUNGSAUTOMATIK_AB` |
 *
 * Beide Rückfälle sind heute derselbe Tag (`CUTOVER_TAG`), und beide Lesarten
 * sind die der Konstante, die der jeweilige Schlüssel ersetzt — der
 * Unterschied ist **geerbt und nicht neu erfunden**. Der Cutover-Kalender
 * benennt ihn seit `E-998` ausdrücklich: `ZAHLUNGSAUTOMATIK_AB` ist «die
 * einzige der fünf Grenzen, die den Tag des LAUFS misst und nicht das
 * Buchungsdatum einer Bewegung». Wer hier einen dritten Schlüssel anlegt,
 * schreibt seine Lesart in diese Tabelle, statt sie aus einem der beiden
 * Nachbarn zu erraten.
 */
export const automatikStartSchluesselSchema = z.enum(['aufladung-bank', 'zahlung-zuordnung']);
/**
 * Woher der geltende Starttag stammt.
 *
 * `vorgabe` — es gibt keine Zeile, es gilt der Rückfall aus dem Cutover-Kalender.
 * `gesetzt` — ein Mensch hat den Tag gesetzt, und `gesetztAm`/`gesetztVon` nennen ihn.
 */
export const automatikStartQuelleSchema = z.enum(['vorgabe', 'gesetzt']);
/**
 * Der geltende Starttag einer Automatik, wie ihn die Auskunft ausgibt.
 *
 * **`abDatum` ist immer gefüllt**, auch wenn keine Zeile existiert — dann
 * trägt es den Rückfall, und `quelle` steht auf `vorgabe`. Ein `null` wäre die
 * bequemere Form und die schlechtere: Jeder Leser müsste den Rückfall selbst
 * kennen, und damit stünde der Cutover-Tag ein zweites Mal in der Oberfläche.
 *
 * **`vorgabe` ist der Rückfallwert selbst** und steht neben `abDatum`, weil
 * die Maske sagen können soll, wohin ein „Zurücksetzen" führte, ohne den Wert
 * abzuschreiben.
 */
export const automatikStartSchema = z
    .object({
    schluessel: automatikStartSchluesselSchema,
    /** Der geltende Tag — gesetzt oder Rückfall, nie leer. */
    abDatum: isoDate,
    quelle: automatikStartQuelleSchema,
    /** Der Rückfallwert aus dem Cutover-Kalender, damit ihn niemand abschreiben muss. */
    vorgabe: isoDate,
    /** `null`, solange `quelle` auf `vorgabe` steht. */
    gesetztAm: z.string().datetime().nullable(),
    /** `null`, solange `quelle` auf `vorgabe` steht. */
    gesetztVon: uuid.nullable(),
})
    .strict();
/**
 * Der Rumpf, mit dem ein Administrator den Starttag setzt.
 *
 * **Nur das Datum, und kein `schluessel` im Rumpf** — der steht im Pfad. Ein
 * Schlüssel an zwei Stellen ist eine Gelegenheit, sie verschieden zu füllen;
 * der Endpunkt müsste dann entscheiden, welcher gilt.
 */
export const automatikStartSetzenSchema = z
    .object({
    abDatum: isoDate,
})
    .strict();
/**
 * Die Auskunft über alle Automatiken des Katalogs.
 *
 * **Eine Liste und keine Einzelabfrage je Schlüssel.** Der Katalog hat heute
 * einen Eintrag und wächst nur über eine Migration; eine Maske, die für jeden
 * Schalter einen eigenen Aufruf bräuchte, wäre bei zwei Einträgen schon
 * falsch gebaut.
 */
export const automatikStartListeSchema = z
    .object({
    eintraege: z.array(automatikStartSchema),
})
    .strict();
//# sourceMappingURL=automatik-start.js.map